// Story 1.31 — admin moderation queue at /admin/moderation.
// Wrapped by <AdminGuard> in App.tsx.
//
// Single list page with per-row inline action confirms (Replace image /
// Suspend creator / Dismiss). No detail page — the row carries enough
// context. Status filter pill row syncs to URL via useSearchParams.

import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@greenhold/components";

import { EmptyState } from "../components/EmptyState";
import {
  DEFAULT_MODERATION_FILTERS,
  type ModerationFlagListItem,
  type ModerationFlagStatus,
  type ModerationFlagTargetKind,
  type ModerationFlagsFilters,
  useAdminModerationFlags,
} from "../features/admin/useAdminModerationFlags";
import {
  ResolveModerationFlagError,
  type ResolveModerationAction,
  useResolveModerationFlag,
} from "../features/admin/useResolveModerationFlag";

// ---------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------

function formatRelative(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const minutes = Math.floor(ms / 60_000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

function targetKindLabel(kind: ModerationFlagTargetKind): string {
  switch (kind) {
    case "creator_image":
      return "profile image";
    case "hello_text":
      return "hello-text bio";
    case "share_certificate":
      return "certificate";
    case "bulletin_post":
      return "bulletin post";
  }
}

function statusBadgeVariant(
  status: "pending" | "actioned" | "dismissed"
): "warning" | "success" | "secondary" {
  switch (status) {
    case "pending":
      return "warning";
    case "actioned":
      return "success";
    case "dismissed":
      return "secondary";
  }
}

function filtersFromSearchParams(
  params: URLSearchParams
): ModerationFlagsFilters {
  const status =
    (params.get("status") as ModerationFlagStatus | null) ??
    DEFAULT_MODERATION_FILTERS.status;
  return {
    status,
    page: Number(params.get("page") ?? "1") || 1,
    page_size:
      Number(
        params.get("page_size") ?? String(DEFAULT_MODERATION_FILTERS.page_size)
      ) || DEFAULT_MODERATION_FILTERS.page_size,
  };
}

const SUSPEND_REASON_MIN = 20;
const SUSPEND_REASON_MAX = 500;

// ---------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------

type DialogState =
  | { kind: "none" }
  | {
      kind: "replace";
      flag: ModerationFlagListItem;
    }
  | {
      kind: "suspend";
      flag: ModerationFlagListItem;
      reason: string;
    }
  | {
      kind: "dismiss";
      flag: ModerationFlagListItem;
    };

export function AdminModerationPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const filters = filtersFromSearchParams(searchParams);
  const query = useAdminModerationFlags(filters);
  const resolve = useResolveModerationFlag();

  const [dialog, setDialog] = useState<DialogState>({ kind: "none" });

  function updateFilter<K extends keyof ModerationFlagsFilters>(
    key: K,
    value: ModerationFlagsFilters[K] | undefined
  ) {
    const next = new URLSearchParams(searchParams);
    if (value === undefined || value === null) {
      next.delete(key);
    } else {
      next.set(key, String(value));
    }
    if (key !== "page") next.delete("page");
    setSearchParams(next);
  }

  function goToPage(page: number) {
    const next = new URLSearchParams(searchParams);
    next.set("page", String(Math.max(1, page)));
    setSearchParams(next);
  }

  async function runAction(
    flag: ModerationFlagListItem,
    action: ResolveModerationAction,
    reason?: string
  ) {
    try {
      const result = await resolve.mutateAsync({
        flag_id: flag.id,
        action,
        reason,
      });
      if (result.data.decision === "dismiss") {
        toast.success("Flag dismissed.");
      } else if (result.data.decision === "replace_image") {
        toast.success(
          `Image removed for ${flag.creator_display_name ?? "creator"}. They've been emailed.`
        );
      } else {
        toast.success(
          `${flag.creator_display_name ?? "Creator"} suspended. They've been emailed.`
        );
      }
      setDialog({ kind: "none" });
    } catch (e) {
      if (e instanceof ResolveModerationFlagError) {
        if (e.code === "MODERATION_FLAG_ALREADY_RESOLVED") {
          toast.error("Another action already resolved this flag — refreshing.");
          setDialog({ kind: "none" });
          return;
        }
        if (e.code === "MODERATION_ACTION_MISMATCH") {
          toast.error(
            `That action can't be applied to a ${e.target_kind ?? "flag"} target.`
          );
          return;
        }
        if (e.code === "MODERATION_SUSPEND_NOT_APPLICABLE") {
          toast.error(
            `Creator is in state '${e.current_status ?? "unknown"}' — can't suspend.`
          );
          return;
        }
        toast.error(e.message);
        return;
      }
      const msg = e instanceof Error ? e.message : "Action failed.";
      toast.error(msg);
    }
  }

  function closeDialog() {
    if (resolve.isPending) return;
    setDialog({ kind: "none" });
  }

  return (
    <main className="min-h-screen bg-brand-paper-200 text-brand-forest-900 p-6">
      <div className="max-w-6xl mx-auto space-y-[var(--gh-rhythm-admin,16px)]">
        <header className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <h1 className="text-3xl font-heading font-bold">Moderation queue</h1>
            <p className="opacity-70 text-sm">
              User-flagged creator content. Action each row by replacing the
              image, suspending the creator, or dismissing the flag.
            </p>
          </div>
          <Button variant="secondary" onClick={() => query.refetch()}>
            Refresh
          </Button>
        </header>

        {/* Status filter pills */}
        <Card>
          <CardContent className="flex flex-wrap gap-2 p-3">
            {(["pending", "actioned", "dismissed", "any"] as const).map(
              (s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => updateFilter("status", s)}
                  className={`px-3 py-1 rounded-full text-sm font-mono uppercase tracking-wider transition ${
                    filters.status === s
                      ? "bg-brand-forest-700 text-brand-paper-50"
                      : "bg-brand-paper-100 hover:bg-brand-paper-200 text-brand-forest-900"
                  }`}
                  aria-pressed={filters.status === s}
                >
                  {s}
                </button>
              )
            )}
          </CardContent>
        </Card>

        {query.isPending ? (
          <Card>
            <CardContent className="p-8 text-center opacity-70">
              Loading…
            </CardContent>
          </Card>
        ) : query.isError ? (
          <Card>
            <CardContent className="p-6 text-center space-y-3">
              <p className="text-destructive">
                {query.error.message ?? "Failed to load moderation queue."}
              </p>
              <Button onClick={() => query.refetch()} variant="secondary">
                Retry
              </Button>
            </CardContent>
          </Card>
        ) : query.data.data.length === 0 ? (
          <EmptyState
            aria-label="No flags awaiting moderation"
            description="No flags awaiting moderation. Visitor + user reports will appear here once they roll in."
          />
        ) : (
          <div className="space-y-3">
            <p className="text-sm opacity-70">
              Showing {query.data.data.length} flag
              {query.data.data.length === 1 ? "" : "s"}
              {filters.status === "pending"
                ? " (oldest first)"
                : " (newest first)"}
            </p>
            {query.data.data.map((flag) => (
              <FlagRow
                key={flag.id}
                flag={flag}
                onReplace={() => setDialog({ kind: "replace", flag })}
                onSuspend={() =>
                  setDialog({ kind: "suspend", flag, reason: "" })
                }
                onDismiss={() => setDialog({ kind: "dismiss", flag })}
              />
            ))}
          </div>
        )}

        {query.data && (query.data.has_next || filters.page > 1) ? (
          <div className="flex items-center justify-end gap-2">
            <Button
              variant="secondary"
              onClick={() => goToPage(filters.page - 1)}
              disabled={filters.page <= 1}
            >
              ← Prev
            </Button>
            <span className="text-sm opacity-70 font-mono">
              Page {filters.page}
            </span>
            <Button
              variant="secondary"
              onClick={() => goToPage(filters.page + 1)}
              disabled={!query.data.has_next}
            >
              Next →
            </Button>
          </div>
        ) : null}
      </div>

      {/* ----- Replace image confirm ----- */}
      <AlertDialog
        open={dialog.kind === "replace"}
        onOpenChange={(open) => !open && closeDialog()}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Replace creator image?</AlertDialogTitle>
            <AlertDialogDescription>
              The image will be removed. The creator will see their
              geometric-initials placeholder until they upload a new one. A
              notification email will be sent. This action cannot be undone via
              the UI.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={resolve.isPending}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                if (dialog.kind === "replace") {
                  void runAction(dialog.flag, "replace_image");
                }
              }}
              disabled={resolve.isPending}
            >
              {resolve.isPending ? "Replacing…" : "Replace image"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* ----- Suspend creator confirm (with reason textarea) ----- */}
      <AlertDialog
        open={dialog.kind === "suspend"}
        onOpenChange={(open) => !open && closeDialog()}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Suspend creator?</AlertDialogTitle>
            <AlertDialogDescription>
              The creator&apos;s status will flip to &lsquo;suspended&rsquo;.
              They cannot edit their profile or upload new images. A
              notification email will be sent with your reason. To reinstate,
              contact engineering.
            </AlertDialogDescription>
          </AlertDialogHeader>
          {dialog.kind === "suspend" ? (
            <div className="px-6 pb-2 space-y-1">
              <label
                htmlFor="suspend-reason"
                className="text-sm font-medium block"
              >
                Reason ({SUSPEND_REASON_MIN}-{SUSPEND_REASON_MAX} chars,
                included in the email)
              </label>
              <textarea
                id="suspend-reason"
                className="w-full border rounded p-2 font-mono text-sm bg-white min-h-24"
                value={dialog.reason}
                maxLength={SUSPEND_REASON_MAX}
                disabled={resolve.isPending}
                onChange={(e) =>
                  setDialog({
                    kind: "suspend",
                    flag: dialog.flag,
                    reason: e.target.value,
                  })
                }
              />
              <p className="text-xs opacity-70 text-right font-mono">
                {dialog.reason.length} / {SUSPEND_REASON_MAX}
              </p>
            </div>
          ) : null}
          <AlertDialogFooter>
            <AlertDialogCancel disabled={resolve.isPending}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                if (dialog.kind === "suspend") {
                  void runAction(
                    dialog.flag,
                    "suspend_creator",
                    dialog.reason.trim()
                  );
                }
              }}
              disabled={
                resolve.isPending ||
                dialog.kind !== "suspend" ||
                dialog.reason.trim().length < SUSPEND_REASON_MIN ||
                dialog.reason.trim().length > SUSPEND_REASON_MAX
              }
            >
              {resolve.isPending ? "Suspending…" : "Suspend creator"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* ----- Dismiss confirm ----- */}
      <AlertDialog
        open={dialog.kind === "dismiss"}
        onOpenChange={(open) => !open && closeDialog()}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Dismiss flag?</AlertDialogTitle>
            <AlertDialogDescription>
              No action will be taken. The flag will be marked resolved. The
              creator will NOT be notified.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={resolve.isPending}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                if (dialog.kind === "dismiss") {
                  void runAction(dialog.flag, "dismiss");
                }
              }}
              disabled={resolve.isPending}
            >
              {resolve.isPending ? "Dismissing…" : "Dismiss flag"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </main>
  );
}

// ---------------------------------------------------------------------
// Row component
// ---------------------------------------------------------------------

function FlagRow({
  flag,
  onReplace,
  onSuspend,
  onDismiss,
}: {
  flag: ModerationFlagListItem;
  onReplace: () => void;
  onSuspend: () => void;
  onDismiss: () => void;
}) {
  const isPending = flag.status === "pending";
  const canReplaceImage = isPending && flag.target_kind === "creator_image";
  const canSuspend =
    isPending &&
    (flag.target_kind === "creator_image" || flag.target_kind === "hello_text");
  const canDismiss = isPending;

  const creatorName = flag.creator_display_name ?? "(unknown)";
  const ariaSuffix = `${creatorName} (flag from ${formatRelative(flag.created_at)})`;

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-3 pb-2">
        <div className="flex items-center gap-2">
          <Badge variant={statusBadgeVariant(flag.status)}>
            {flag.status.toUpperCase()}
          </Badge>
          <span className="text-xs opacity-70 font-mono">
            {formatRelative(flag.created_at)}
          </span>
        </div>
        <CardTitle className="text-sm font-mono">
          <span className="opacity-60">flag</span> {flag.id.slice(0, 8)}…
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-sm">
          Flagged target:{" "}
          <strong>{creatorName}</strong>&apos;s{" "}
          <em>{targetKindLabel(flag.target_kind)}</em>
        </p>
        <p className="text-sm opacity-80">
          Reported by <strong>{flag.flagger_label}</strong>
        </p>
        <p className="text-sm">
          Reason:{" "}
          {flag.reason ? (
            <span>{flag.reason}</span>
          ) : (
            <em className="opacity-70">No reason provided</em>
          )}
        </p>

        {/* Preview */}
        {flag.target_kind === "creator_image" ? (
          flag.preview_signed_url ? (
            <img
              src={flag.preview_signed_url}
              alt={`${creatorName}'s current profile image`}
              className="w-32 h-32 object-cover rounded border"
            />
          ) : (
            <p className="opacity-70 text-sm">Image already removed.</p>
          )
        ) : flag.target_kind === "hello_text" ? (
          <blockquote className="border-l-4 border-warning pl-3 text-sm whitespace-pre-wrap">
            {flag.hello_text_preview ?? "(no current hello-text)"}
          </blockquote>
        ) : (
          <p className="opacity-70 text-sm">
            Preview not yet supported for{" "}
            <code>{flag.target_kind}</code> targets. target_id:{" "}
            <code className="font-mono">{flag.target_id}</code>
          </p>
        )}

        {/* Action buttons */}
        {isPending ? (
          <div className="flex flex-wrap gap-2 pt-2">
            <Button
              variant="secondary"
              disabled={!canReplaceImage}
              onClick={onReplace}
              aria-label={`Replace image with placeholder for ${ariaSuffix}`}
            >
              Replace image with placeholder
            </Button>
            <Button
              variant="destructive"
              disabled={!canSuspend}
              onClick={onSuspend}
              aria-label={`Suspend creator ${ariaSuffix}`}
            >
              Suspend creator
            </Button>
            <Button
              variant="tertiary"
              disabled={!canDismiss}
              onClick={onDismiss}
              aria-label={`Dismiss flag ${ariaSuffix}`}
            >
              Dismiss flag
            </Button>
          </div>
        ) : (
          <p className="text-xs opacity-60 font-mono pt-1">
            Resolved{" "}
            {flag.reviewed_at ? formatRelative(flag.reviewed_at) : "—"}
            {flag.action_taken ? ` · ${flag.action_taken}` : ""}
          </p>
        )}
      </CardContent>
    </Card>
  );
}
